import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, CreditCard, Truck } from 'lucide-react';
import { orderAPI } from '../api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

const STATUS_STEPS = ['pending', 'confirmed', 'packed', 'shipped', 'delivered'];
const VALID_NEXT = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['packed', 'cancelled'],
  packed: ['shipped', 'cancelled'],
  shipped: ['delivered'],
  delivered: [],
  cancelled: [],
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    orderAPI.getById(id)
      .then(res => { setOrder(res.data.order); setTrackingNumber(res.data.order.trackingNumber || ''); })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [id]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await orderAPI.updateStatus(id, { orderStatus: newStatus, trackingNumber, note });
      setOrder(res.data.order);
      toast.success(`Order marked as "${newStatus}"`);
      setNote('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setUpdating(false); }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '80px', color: '#94a3b8' }}>
      <div style={{ width: '36px', height: '36px', border: '3px solid #e2e8f0', borderTopColor: '#14b8a6', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
      Loading order...
    </div>
  );

  if (!order) return <div style={{ textAlign: 'center', padding: '80px', color: '#94a3b8' }}>Order not found</div>;

  const currentIdx = STATUS_STEPS.indexOf(order.orderStatus);
  const nextStatuses = VALID_NEXT[order.orderStatus] || [];

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <button onClick={() => navigate('/orders')} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
            <ArrowLeft size={16} /> Back to Orders
          </button>
          <h1 className="page-title">Order #{order.orderId}</h1>
          <p className="page-subtitle">{dayjs(order.createdAt).format('DD MMM YYYY, h:mm A')}</p>
        </div>
        <span className={`badge badge-${order.orderStatus}`} style={{ fontSize: '14px', padding: '8px 16px' }}>{order.orderStatus}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Progress tracker */}
          {order.orderStatus !== 'cancelled' && (
            <div className="glass-card" style={{ padding: '24px' }}>
              <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '20px' }}>📦 Order Progress</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                {STATUS_STEPS.map((s, i) => (
                  <div key={s} style={{ display: 'flex', alignItems: 'center', flex: i < STATUS_STEPS.length - 1 ? 1 : 0 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                      <div style={{
                        width: '32px', height: '32px', borderRadius: '50%',
                        background: i <= currentIdx ? '#14b8a6' : '#e2e8f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: i <= currentIdx ? 'white' : '#94a3b8',
                        fontWeight: '700', fontSize: '12px', transition: 'all 0.3s ease',
                      }}>
                        {i <= currentIdx ? '✓' : i + 1}
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: '600', color: i <= currentIdx ? '#14b8a6' : '#94a3b8', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                        {s}
                      </span>
                    </div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div style={{ flex: 1, height: '2px', background: i < currentIdx ? '#14b8a6' : '#e2e8f0', transition: 'background 0.3s ease', margin: '0 4px', marginBottom: '18px' }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Products */}
          <div className="glass-card" style={{ padding: '24px' }}>
            <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>🛒 Products</h3>
            {order.products?.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px', padding: '14px 0', borderBottom: i < order.products.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                {p.image ? (
                  <img src={p.image} alt="" style={{ width: '56px', height: '56px', borderRadius: '10px', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '56px', height: '56px', borderRadius: '10px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Package size={22} color="#94a3b8" />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#0f172a' }}>{p.productName}</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>SKU: {p.sku} · Qty: {p.quantity}</div>
                </div>
                <div style={{ fontWeight: '700', color: '#0f172a' }}>₹{(p.price * p.quantity).toLocaleString()}</div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '16px', borderTop: '2px solid #f1f5f9' }}>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Total Amount: </span>
                <strong style={{ fontSize: '20px', color: '#0f172a' }}>₹{order.totalAmount?.toLocaleString()}</strong>
              </div>
            </div>
          </div>

          {/* Status history */}
          {order.statusHistory?.length > 0 && (
            <div className="glass-card" style={{ padding: '24px' }}>
              <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>📋 Status History</h3>
              {[...order.statusHistory].reverse().map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#14b8a6', marginTop: '6px', flexShrink: 0 }} />
                  <div>
                    <span className={`badge badge-${h.status}`}>{h.status}</span>
                    {h.note && <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{h.note}</p>}
                    <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{dayjs(h.updatedAt).format('DD MMM YYYY, h:mm A')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Update status */}
          {nextStatuses.length > 0 && (
            <div className="glass-card" style={{ padding: '24px' }}>
              <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>🔄 Update Status</h3>
              {order.orderStatus === 'packed' && (
                <div style={{ marginBottom: '12px' }}>
                  <label className="form-label">Tracking Number</label>
                  <input className="form-input" value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="Enter tracking number" />
                </div>
              )}
              <div style={{ marginBottom: '12px' }}>
                <label className="form-label">Note (Optional)</label>
                <input className="form-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note..." />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {nextStatuses.map((s) => (
                  <button key={s} onClick={() => updateStatus(s)} disabled={updating} className="btn-primary"
                    style={{ justifyContent: 'center', background: s === 'cancelled' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : undefined }}>
                    {updating ? <div className="spinner" /> : `Mark as "${s}"`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Customer info */}
          <div className="glass-card" style={{ padding: '24px' }}>
            <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>👤 Customer</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div><span style={{ fontSize: '12px', color: '#94a3b8' }}>Name</span><p style={{ fontWeight: '600' }}>{order.customerName}</p></div>
              {order.customerEmail && <div><span style={{ fontSize: '12px', color: '#94a3b8' }}>Email</span><p>{order.customerEmail}</p></div>}
              {order.customerPhone && <div><span style={{ fontSize: '12px', color: '#94a3b8' }}>Phone</span><p>{order.customerPhone}</p></div>}
            </div>
          </div>

          {/* Shipping address */}
          <div className="glass-card" style={{ padding: '24px' }}>
            <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>📍 Shipping Address</h3>
            <p style={{ color: '#334155', lineHeight: '1.7', fontSize: '14px' }}>
              {order.shippingAddress?.street && <>{order.shippingAddress.street}<br /></>}
              {order.shippingAddress?.city && <>{order.shippingAddress.city}, {order.shippingAddress.state}<br /></>}
              {order.shippingAddress?.pincode}
            </p>
            {order.trackingNumber && (
              <div style={{ marginTop: '14px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700', marginBottom: '2px' }}>TRACKING NUMBER</div>
                <div style={{ fontFamily: 'monospace', fontWeight: '700', color: '#0f172a' }}>{order.trackingNumber}</div>
              </div>
            )}
          </div>

          {/* Payment */}
          <div className="glass-card" style={{ padding: '24px' }}>
            <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>💳 Payment</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>Payment Status</span>
              <span className={`badge badge-${order.paymentStatus}`}>{order.paymentStatus}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>Method</span>
              <span style={{ fontWeight: '600', color: '#0f172a', textTransform: 'capitalize' }}>
                {order.paymentMethod ? order.paymentMethod.replace(/_/g, ' ') : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
